import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { PanelHost } from '@fias/panel-kit';
import { ConfirmDialogProvider } from '@/components/ui/confirm-dialog';
import { initTelemetry, ErrorBoundary } from '@/lib/telemetry';
import { initAnalytics } from '@/lib/analytics';
import { initAttribution } from '@/lib/attribution';
import { API_BASE } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { getConsent, subscribe as subscribeConsent } from '@/lib/cookie-consent';
import App from './App';
import { EmbedApp } from '@/arche/embed/EmbedApp';
import { IS_EMBED_DOCUMENT } from '@/lib/embed-document';
import './index.css';

// Side-effect import: registers every executionMode: 'client-side' entity
// definition (from @fias/client-entities) against the client-entity registry
// before any arche renders. See client/src/lib/register-client-entities.ts.
import '@/lib/register-client-entities';

// Initialize telemetry before rendering. Error/performance telemetry
// runs under legitimate interest for security and operational integrity
// and is NOT gated by the cookie banner.
initTelemetry();

// Platform usage analytics is "optional" under the cookie banner — only
// initialize once the user has accepted. If they decline or haven't
// decided, we skip it; subscribing below picks up a later acceptance.
let analyticsInitialized = false;
function maybeInitAnalytics() {
  if (analyticsInitialized) return;
  if (getConsent().status !== 'accepted') return;
  analyticsInitialized = true;
  // apiBase MUST match the rest of the app (api.ts API_BASE) so analytics
  // POSTs route to the API origin — VITE_API_URL in prod, /v1 with Vite
  // proxy locally and in Codespaces. The user_id provider is wired later
  // when the auth context settles; calling init at consent time is
  // intentional so anonymous events on pre-login pages still get captured.
  initAnalytics({ apiBase: API_BASE });
}
maybeInitAnalytics();
subscribeConsent(maybeInitAnalytics);

// Acquisition attribution (AdOpt) — captures ad-click/UTM/referrer landing
// context. Handles consent internally: an explicit "essential" decline
// purges state and disables capture; see client/src/lib/attribution.ts for
// the full consent posture. Must run at bootstrap so the landing URL's
// query params are still present.
initAttribution({ apiBase: API_BASE });

function FallbackError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-foreground mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">Please try refreshing the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary fallback={<FallbackError />}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          {/* Panels — the shared declarative panel renderer's MODAL presenter
              (@fias/panel-kit). Mounted once at the root so any arche can call
              `usePanel().showPanel(...)`; it renders nothing until a panel is
              shown. Inline panels need no host at all — they are just <Panel>
              in the page. See docs/panels.md. */}
          <PanelHost>
            {/* The in-app confirm() every destructive action asks through
                (`useConfirm()`), mounted once at the root like PanelHost so any
                arche or screen can ask. Never `window.confirm`: a browser can
                suppress it and it then answers "no" silently. */}
            <ConfirmDialogProvider>
              {/* A framed document gets the embed tree and nothing else: the
                  edge lets other sites frame `/a/<slug>?embed=true`, and this is
                  where "only an embeddable arche, only signed out" is enforced.
                  See lib/embed-document.ts. */}
              {IS_EMBED_DOCUMENT ? <EmbedApp /> : <App />}
            </ConfirmDialogProvider>
          </PanelHost>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
